import { PlatformSiteAri } from '@atlassian/ari';
import { EntryPointRef } from '@atlassian/entry-point-types';
import { globalStarredQueryBuilder } from '@atlassian/home-starred/cypher-query';
import { starredListEntryPoint } from '@atlassian/home-starred/entrypoint';
import { loadEntryPoint } from 'react-relay';

import { getDefaultTabKey } from '@townsquare/hometab-saved-tab';
import { collabContextHasJira } from '@townsquare/hometab-saved-tab/has-jira';
import { getAggRelayEnvironment } from '@townsquare/relay-utils/agg-environment';
import { createResource } from '@townsquare/router/resources/createResource';
import type { RouteResource } from '@townsquare/router/routing-types';
import type { ResourceContext } from '@townsquare/router/types';
import { expValWithoutExposure } from '@townsquare/stat-sig';
import { fg } from '@townsquare/stat-sig/gate';

const isStarredTabInitially = (resourceContext: ResourceContext): boolean => {
	const assignedEnabled =
		collabContextHasJira(resourceContext as ResourceContext) &&
		// Only calling without exposure here defensively. Exposures will be triggered in the UI code.
		expValWithoutExposure('townsquare_assigned_list', 'isEnabled', false);

	const defaultTab = getDefaultTabKey(assignedEnabled);

	return defaultTab === 'starred';
};

type PlatformStarredData = EntryPointRef<typeof starredListEntryPoint>;

export const platformStarredFeedResource: RouteResource<PlatformStarredData | undefined> =
	createResource<PlatformStarredData | undefined>({
		type: 'STARRED',
		getKey: () => 'platform-starred-resource',
		getData: async (routerContext, resourceContext) => {
			if (!fg('townsquare_platform_starred_list')) {
				return;
			}

			const { workspace, user } = resourceContext as ResourceContext;

			if (!workspace?.cloudId || !user.accountId) {
				return;
			}

			const isStarredPage = routerContext.route.name === 'starred';

			const isHometabStarred =
				routerContext.route.name === 'hometab' &&
				isStarredTabInitially(resourceContext as ResourceContext);

			if (!isStarredPage && !isHometabStarred) {
				return;
			}

			let collabContextId;

			try {
				collabContextId = PlatformSiteAri.create({ siteId: workspace.cloudId }).toString();
			} catch {
				return;
			}

			const reference = loadEntryPoint(
				{ getEnvironment: getAggRelayEnvironment },
				starredListEntryPoint,
				{
					cypherQuery: globalStarredQueryBuilder(`ari:cloud:identity::user/${user.accountId}`),
					queryContext: collabContextId,
				},
			);

			return reference;
		},
	});
